/**
 * Stop Sequence Editor Component
 *
 * Compact, single-row-per-stop table. Drag the handle to reorder; the travel-time chip on each
 * row is the leg to the NEXT stop (click to edit). Designed to be dense so the whole route reads
 * at a glance without scrolling.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import { Icon } from '@liftango/ui-icons';
import { useDrag, useDrop } from 'react-dnd';
import type { FixedRouteStopConfig, FixedRouteSegment } from '../../types/fixedRoutes';

const STOP_ITEM_TYPE = 'ROUTE_STOP';

interface StopSequenceEditorProps {
  stops: FixedRouteStopConfig[];
  allStops: Array<{ id: string; name: string }>;
  onReorder?: (stops: FixedRouteStopConfig[]) => void;
  onRemove: (index: number) => void;
  onUpdateWaitTime: (index: number, waitTimeMinutes: number) => void;
  onUpdateTravelTime: (fromStopId: string, toStopId: string, travelTimeMinutes: number) => void;
  segments?: FixedRouteSegment[];
  trafficMultiplier?: number;
  waitInputsEnabled?: boolean;
}

interface StopRowProps {
  stop: FixedRouteStopConfig;
  stopName: string;
  index: number;
  isLast: boolean;
  /** Travel minutes to the next stop, already adjusted for traffic (for display). */
  travelDisplay?: number;
  /** Raw travel minutes to the next stop (used to seed the edit field). */
  travelRaw?: number;
  nextStopId?: string;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onRemove: (index: number) => void;
  onUpdateWaitTime: (index: number, waitTimeMinutes: number) => void;
  onUpdateTravelTime: (fromStopId: string, toStopId: string, travelTimeMinutes: number) => void;
  waitInputsEnabled: boolean;
}

function StopRow({
  stop,
  stopName,
  index,
  isLast,
  travelDisplay,
  travelRaw,
  nextStopId,
  onReorder,
  onRemove,
  onUpdateWaitTime,
  onUpdateTravelTime,
  waitInputsEnabled,
}: StopRowProps) {
  const { t } = useTranslation('fixedRoutes');
  const [editingTravel, setEditingTravel] = React.useState(false);
  const [travelInput, setTravelInput] = React.useState('');

  const [{ isDragging }, drag, preview] = useDrag({
    type: STOP_ITEM_TYPE,
    item: { index, stopId: stop.stopId },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: STOP_ITEM_TYPE,
    collect: (monitor) => ({ isOver: monitor.isOver() }),
    drop: (item: { index: number; stopId: string }) => {
      if (item.index !== index) onReorder(item.index, index);
    },
  });

  // Drag is started only from the handle, but the whole row is the drag preview + drop target.
  const rowRef = React.useCallback((node: HTMLDivElement | null) => {
    if (node) {
      drop(node);
      preview(node);
    }
  }, [drop, preview]);
  const handleRef = React.useCallback((node: HTMLDivElement | null) => {
    if (node) drag(node);
  }, [drag]);

  const commitTravel = () => {
    const value = parseFloat(travelInput);
    if (!Number.isNaN(value) && value >= 0 && nextStopId) {
      onUpdateTravelTime(stop.stopId, nextStopId, value);
    }
    setEditingTravel(false);
    setTravelInput('');
  };

  return (
    <Box
      ref={rowRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.5,
        minHeight: 38,
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
        backgroundColor: isOver ? 'action.selected' : 'transparent',
        opacity: isDragging ? 0.4 : 1,
        transition: 'background-color 0.15s ease',
        '&:hover': { backgroundColor: 'action.hover' },
        '&:hover .stop-row-remove': { opacity: 1 },
      }}
    >
      <Box
        ref={handleRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: isDragging ? 'grabbing' : 'grab',
          color: 'text.disabled',
          '&:hover': { color: 'text.secondary' },
        }}
        aria-label={t('stopSequence.stopLabel', { number: index + 1 })}
      >
        <Icon name="grip-vertical" size={20} />
      </Box>

      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.72rem',
          fontWeight: 700,
        }}
      >
        {index + 1}
      </Box>

      <Tooltip title={stopName} enterDelay={600}>
        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {stopName}
        </Typography>
      </Tooltip>

      {waitInputsEnabled && (
        <Tooltip title={t('stopSequence.waitLabel')} enterDelay={400}>
          <TextField
            type="number"
            size="small"
            value={stop.waitTimeMinutes}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (!Number.isNaN(value) && value >= 0) onUpdateWaitTime(index, value);
            }}
            inputProps={{ min: 0, max: 60, step: 0.5, style: { padding: '2px 6px', fontSize: '0.72rem', width: 38 } }}
            sx={{ '& .MuiInputBase-root': { height: 24 } }}
            onClick={(e) => e.stopPropagation()}
          />
        </Tooltip>
      )}

      {/* Travel time to the NEXT stop (click to edit). Hidden on the last row. */}
      {!isLast && (
        editingTravel ? (
          <TextField
            type="number"
            size="small"
            autoFocus
            value={travelInput}
            onChange={(e) => setTravelInput(e.target.value)}
            onBlur={commitTravel}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitTravel();
              if (e.key === 'Escape') {
                setEditingTravel(false);
                setTravelInput('');
              }
            }}
            inputProps={{ min: 0, max: 999, step: 0.5, style: { padding: '2px 6px', fontSize: '0.72rem', width: 44 } }}
            sx={{ '& .MuiInputBase-root': { height: 24 } }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <Tooltip title={t('stopSequence.travelTimeAria', { from: index + 1, to: index + 2 })} enterDelay={400}>
            <Chip
              size="small"
              color={travelDisplay !== undefined ? 'primary' : 'default'}
              variant={travelDisplay !== undefined ? 'filled' : 'outlined'}
              label={travelDisplay !== undefined ? t('stopSequence.travelMinutes', { value: travelDisplay }) : t('stopSequence.noTravelTime')}
              onClick={() => {
                setEditingTravel(true);
                setTravelInput(travelRaw?.toString() ?? '');
              }}
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                cursor: 'pointer',
                flexShrink: 0,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          </Tooltip>
        )
      )}

      <IconButton
        size="small"
        className="stop-row-remove"
        onClick={() => onRemove(index)}
        sx={{ color: 'error.main', p: 0.25, flexShrink: 0, opacity: 0.55, transition: 'opacity 0.15s ease' }}
        aria-label={t('stopSequence.removeAria', { name: stopName })}
      >
        <Icon name="xmark" size={20} />
      </IconButton>
    </Box>
  );
}

export function StopSequenceEditor({
  stops,
  allStops,
  onReorder,
  onRemove,
  onUpdateWaitTime,
  onUpdateTravelTime,
  segments = [],
  trafficMultiplier = 1.0,
  waitInputsEnabled = false,
}: StopSequenceEditorProps) {
  const { t } = useTranslation('fixedRoutes');

  const handleReorder = (fromIndex: number, toIndex: number) => {
    if (onReorder) {
      const newStops = [...stops];
      const [removed] = newStops.splice(fromIndex, 1);
      newStops.splice(toIndex, 0, removed);
      onReorder(newStops);
    }
  };

  const getStopName = (stopId: string) => allStops.find((s) => s.id === stopId)?.name || stopId;

  const getTravelTimeToNext = (stopId: string, index: number): number | undefined => {
    if (index >= stops.length - 1) return undefined;
    const nextStopId = stops[index + 1].stopId;
    const segment = segments.find((s) => s.fromStopId === stopId && s.toStopId === nextStopId);
    return segment?.travelTimeMinutes;
  };

  if (stops.length === 0) {
    return (
      <Box
        sx={{
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('stopSequence.emptyTitle')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      {stops.map((stop, index) => {
        const travelTime = getTravelTimeToNext(stop.stopId, index);
        const adjustedTravelTime = travelTime !== undefined ? Math.round(travelTime * trafficMultiplier) : undefined;
        return (
          <StopRow
            key={`${stop.stopId}-${index}`}
            stop={stop}
            stopName={getStopName(stop.stopId)}
            index={index}
            isLast={index === stops.length - 1}
            travelDisplay={adjustedTravelTime}
            travelRaw={travelTime}
            nextStopId={index < stops.length - 1 ? stops[index + 1].stopId : undefined}
            onReorder={handleReorder}
            onRemove={onRemove}
            onUpdateWaitTime={onUpdateWaitTime}
            onUpdateTravelTime={onUpdateTravelTime}
            waitInputsEnabled={waitInputsEnabled}
          />
        );
      })}
    </Box>
  );
}
